<template>
  <div
    ref="autocompleteContainerRef"
    class="standard-autocomplete"
  >
    <span
      v-if="feedback"
      :class="feedbackClasses"
    >
      {{ feedback }}
    </span>
    <PVAutoComplete
      ref="pvAutoCompleteRef"
      v-bind="attrs"
      v-model="pvModelValue"
      :suggestions="filteredSuggestions"
      :option-label="getItemLabel"
      :data-key="keyProperty"
      :placeholder="resolvedPlaceholder"
      :disabled="disabled"
      :complete-on-focus="true"
      :dropdown="false"
      :delay="0"
      :invalid="state === false"
      :option-disabled="getIsOptionDisabled"
      :pt="passThroughProps"
      :focus-on-hover="false"
      append-to="self"
      fluid
      class="flex-grow-1"
      @click="onInputClick"
      @show="onShow"
      @blur="onBlur"
      @hide="onHide"
      @option-select="onOptionSelect"
    >
      <template #option="{ option }">
        <div
          class="flex-grow-1"
          :class="{ 'standard-autocomplete-group-header': getIsGroupHeader(option) }"
        >
          <slot
            name="suggestion"
            :data="option"
          >
            {{ getItemLabel(option) }}
          </slot>
        </div>
      </template>
    </PVAutoComplete>
  </div>
</template>
<script lang="ts">
export default {
  name: 'StandardAutoComplete',
  compatConfig: { MODE: 3 },
  inheritAttrs: false,
};
</script>
<script setup lang="ts" generic="T extends object">
import { computed, onBeforeUnmount, ref, useAttrs, type Ref } from 'vue';
import { useFocusWithin } from '@vueuse/core';
import { debounce } from 'lodash';
import useTranslation from '@/composables/useTranslation';
import { type PassThrough } from '@primevue/core';
import type PVAutoComplete from 'primevue/autocomplete';
import { type AutoCompletePassThroughOptions } from 'primevue/autocomplete';

/**
 * Why the overlay was closed; gates re-open logic in `onHide`.
 * - `'dismiss'`          – explicit close (blur, empty-query Escape); do not reopen.
 * - `'selection'`        – option was confirmed; do not reopen.
 * - `'suggestion-click'` – mousedown on an option; blur must not close prematurely.
 * - `'escape-with-query'`– Escape while query was non-empty; query was cleared, reopen overlay.
 */
type OverlayHideIntent = 'dismiss' | 'selection' | 'suggestion-click' | 'escape-with-query' | null;

/** Delays analytics/server-side search triggers to avoid firing on every keystroke. */
const INPUT_CHANGE_DEBOUNCE_MS = 1500;

/** Strips diacritics and lowercases a string so accent-insensitive matching works. */
const normalizeForSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const props = withDefaults(
  defineProps<{
    /**
     * The currently selected item. `null` when nothing is selected.
     * Supports `v-model` via `update:modelValue`.
     *
     * TODO: should make required and revisit usage of on-hit event in the future
     */
    modelValue?: T | null;
    /** Current text value of the input. Supports v-model via `update:query`. */
    query: string;
    /** Property name PrimeVue uses to identify items and this component uses to match disabled items. */
    keyProperty: string;
    /** Full list of items available to display and search. */
    items: T[];
    /**
     * Additional visibility predicate applied before matching the query.
     * Return `true` to keep an item in the suggestion list.
     */
    itemVisibleFn?: (item: T) => boolean;
    /** Input placeholder text. Falls back to the global keyword-search translation. */
    placeholder?: string;
    /** Additional CSS classes applied to the underlying `<input>` element. */
    inputClass?: string[];
    /**
     * Validation state. `true` → valid feedback, `false` → PrimeVue invalid styling
     * (and invalid feedback), `undefined` → no validation styling.
     */
    state?: boolean | undefined;
    /** Validation feedback message rendered below the input. */
    feedback?: string;
    /**
     * Converts an item to its display string. Passed directly to PrimeVue as `option-label`,
     * so it controls how items in the dropdown list and the form input value (after selection) are shown
     * Falls back to `item[keyProperty]` when omitted.
     */
    itemLabelFn?: (item: T) => string;
    /**
     * Converts an item to the text matched against the query. Falls back to the item label when omitted.
     * This is meant to be used when the search text is not the same as the display text.
     */
    itemSearchTextFn?: (item: T) => string;
    disabled?: boolean;
    /**
     * Maximum number of suggestions shown in the dropdown. Defaults to 10.
     * If set to 0, all suggestions will be shown.
     */
    maxMatches?: number;
    /**
     * Items that should appear in the dropdown but cannot be selected.
     * Compared against other items using `keyProperty`.
     */
    disabledValues?: T[];
    /**
     * Unselectable section headers in the suggestion list.
     * Styled with autocomplete submenu-label tokens. Distinct from `disabledValues`, which stay faded.
     */
    groupHeaderValues?: T[];
    showSelectedItemInInput?: boolean;
  }>(),
  {
    modelValue: null,
    inputClass: () => [],
    state: undefined,
    feedback: '',
    disabled: false,
    maxMatches: 10,
    disabledValues: () => [],
    groupHeaderValues: () => [],
    showSelectedItemInInput: false,
  },
);

const emit = defineEmits<{
  /**
   * Two-way binding for the selected item.
   * Emits the selected `T` on option select, or `null` when the user starts typing again.
   */
  'update:modelValue': [value: T | null];
  /** Two-way binding for the raw input text. */
  'update:query': [value: string];
  /**
   * Fired when the user selects an option.
   * The payload merges the selected item with a `searchTerm` field containing the text
   * that was in the input at the moment of selection.
   */
  'on-hit': [value: T & { searchTerm: string }];
  /**
   * Debounced (1500 ms) event fired as the user types.
   * Intended for analytics or server-side search triggers — prefer `update:query` for
   * driving immediate UI changes.
   */
  'on-input-change': [value: string];
}>();

defineSlots<{
  suggestion?: (props: { data: T }) => unknown;
}>();

const attrs = useAttrs();
const { translate } = useTranslation();

const autocompleteContainerRef = ref<HTMLElement | null>(null);
const pvAutoCompleteRef = ref<
  InstanceType<typeof PVAutoComplete> & {
    show: () => void;
    hide: () => void;
    d_value: unknown;
    scrollHandler?: { unbindScrollListener: () => void };
  }
>();

const { focused: isFocusWithinAutocomplete } = useFocusWithin(autocompleteContainerRef as Ref<HTMLElement>);

const overlayHideIntent = ref<OverlayHideIntent>(null);

/**
 * Whether the overlay is open.
 */
const isOverlayOpen = ref(false);

const debouncedTrack = debounce((input: string) => {
  emit('on-input-change', input);
}, INPUT_CHANGE_DEBOUNCE_MS);

/**
 * Utility function to cast the item to a record.
 * @param item - The item to cast to a record.
 * @returns The item as a record.
 */
const asRecord = (item: T): Record<string, unknown> => item as unknown as Record<string, unknown>;

const getItemKey = (item: T): string | null => {
  const key = asRecord(item)[props.keyProperty];
  if (key === undefined || key === null) return null;

  return String(key);
};

/**
 *
 * @param item - The item to get the label for.
 * @returns The label for the item. If an itemLabelFn is provided, it uses the itemLabelFn function.
 * Otherwise, it uses the keyProperty of the item. If the keyProperty is not defined, it uses the JSON.stringify of the item.
 */
const getItemLabel = (item: T): string => {
  if (props.itemLabelFn) return props.itemLabelFn(item);

  return String(asRecord(item)[props.keyProperty] ?? JSON.stringify(item));
};

/**
 *
 * @param item - The item to get the search text for.
 * @returns The search text for the item. If a search text function is provided, it uses the function.
 * Otherwise, it falls back to getItemLabel.
 */
const getItemSearchText = (item: T): string => {
  if (props.itemSearchTextFn) return props.itemSearchTextFn(item);

  return getItemLabel(item);
};

/**
 * PVAutoComplete uses a single v-model that is either a string (while the user types) or
 * the selected option object (after selection). We bridge that into two separate props:
 * `query` (the raw input text) and `modelValue` (the confirmed selection).
 *
 * When `showSelectedItemInInput` is true and the overlay is closed, the getter returns the
 * selected item's label so the input displays it visually — without touching `query` or
 * `modelValue`. The setter is unchanged; it still emits the same events as always.
 */
const pvModelValue = computed({
  get(): string | T {
    if (props.showSelectedItemInInput && props.modelValue != null && !isOverlayOpen.value) {
      return getItemLabel(props.modelValue);
    }

    return props.query;
  },
  set(value: string | T | null): void {
    if (typeof value === 'string') {
      emit('update:query', value);

      debouncedTrack(value);
      return;
    }

    if (value === null) {
      emit('update:query', '');
      emit('update:modelValue', null);
      return;
    }

    // T (object) case: not handling here, it will be handled in `onOptionSelect`
    // `onOptionSelect` fires next and emits update:modelValue + on-hit.
  },
});

const isItemInValueList = (item: T, values: T[]): boolean => {
  const itemKey = getItemKey(item);
  if (itemKey === null) return false;

  return values.some((value) => getItemKey(value) === itemKey);
};

/**
 * Checks if an item is a styled section header (not selectable).
 */
const getIsGroupHeader = (item: T): boolean => isItemInValueList(item, props.groupHeaderValues);

/**
 * Checks if an item is disabled.
 * @param item - The item to check if it is disabled.
 * @returns True if the item is disabled, false otherwise.
 */
const getIsOptionDisabled = (item: T): boolean => {
  const itemKey = getItemKey(item);

  if (itemKey === null) return true;

  return isItemInValueList(item, props.disabledValues) || isItemInValueList(item, props.groupHeaderValues);
};

const resolvedPlaceholder = computed((): string =>
  props.modelValue
    ? getItemLabel(props.modelValue)
    : (props.placeholder ?? translate({ path: 'GLOBAL.PLACEHOLDERS.KEYWORD_SEARCH' })),
);

const filteredSuggestions = computed((): T[] => {
  let filteredResults = props.items;

  if (props.itemVisibleFn) {
    filteredResults = filteredResults.filter(props.itemVisibleFn);
  }

  const normalizedQuery = normalizeForSearch(props.query.trim());

  if (normalizedQuery.length > 0) {
    filteredResults = filteredResults.filter((item) =>
      normalizeForSearch(getItemSearchText(item)).includes(normalizedQuery),
    );
  }

  // maxMatches === 0 is the opt-out sentinel meaning "show all".
  if (props.maxMatches === 0) return filteredResults;

  return filteredResults.slice(0, props.maxMatches);
});

/**
 * Marks an enabled suggestion mousedown so `onBlur` does not close the overlay prematurely.
 *
 * Selecting an option blurs the input before `option-select` runs. We hide the overlay on blur
 * because PrimeVue does not do that by default. Without this intent, `onBlur` would hide the
 * panel in the same tick and swallow the click. `onOptionSelect` then advances the intent to
 * `selection`. Disabled options must not set this intent — they never reach `selection`, which
 * would leave a stale `suggestion-click` and keep the dropdown open after focus leaves.
 */
const onSuggestionMousedown = (isDisabled: boolean): void => {
  if (isDisabled) return;

  overlayHideIntent.value = 'suggestion-click';
};

/** Adds caller classes and behavior hooks without overriding themed component styles. */
const passThroughProps = computed(
  (): PassThrough<AutoCompletePassThroughOptions> => ({
    pcInputText: {
      root: {
        class: props.inputClass,
        onKeydown: onInputKeydown,
      },
    },
    option: ({ context }: { context: { disabled: boolean; focused: boolean } }) => ({
      onMousedown: (): void => onSuggestionMousedown(context.disabled),
    }),
  }),
);

const feedbackClasses = computed(
  (): Record<string, boolean> => ({
    'valid-feedback': props.state === true,
    'invalid-feedback': props.state === false,
    'd-block': props.state !== undefined,
  }),
);

const isBrowsingWithEmptyQuery = computed((): boolean => props.query === '');

const shouldReopenOverlayAfterHide = computed(
  (): boolean => isBrowsingWithEmptyQuery.value && isFocusWithinAutocomplete.value,
);

/**
 * Shows the overlay when the input is clicked.
 */
const onInputClick = (): void => {
  pvAutoCompleteRef.value?.show();
};

/**
 * Closes the overlay when the input loses focus. PrimeVue does not hide on blur by default.
 */
const onBlur = (): void => {
  setTimeout(() => {
    if (overlayHideIntent.value === 'suggestion-click') {
      overlayHideIntent.value = null;
      return;
    }

    if (overlayHideIntent.value === 'selection') return;

    overlayHideIntent.value = 'dismiss';
    pvAutoCompleteRef.value?.hide();
  }, 0);
};

const onOptionSelect = ({ value }: { value: T }): void => {
  overlayHideIntent.value = 'selection';

  // The search term is the text that was in the input at the moment of selection.
  // clone the query to avoid mutating the original value
  const searchTerm = props.query;

  // emit the events to the parent component
  emit('update:query', '');
  emit('update:modelValue', value);
  emit('on-hit', { ...value, searchTerm });

  // Force-clear PrimeVue's internal d_value so the input shows '' even when props.query
  // was already '' before selection (in which case the computed getter produces no change
  // and PrimeVue's d_value would stay set to the selected object).
  if (pvAutoCompleteRef.value) {
    pvAutoCompleteRef.value.d_value = '';
  }
};

const onHide = (): void => {
  const intent = overlayHideIntent.value;
  overlayHideIntent.value = null;

  if (intent === 'escape-with-query') {
    pvAutoCompleteRef.value?.show();
    return;
  }

  if (!intent && shouldReopenOverlayAfterHide.value) {
    pvAutoCompleteRef.value?.show();
    return;
  }

  isOverlayOpen.value = false;

  if (props.showSelectedItemInInput && props.query !== '') {
    emit('update:query', '');
  }
};

const onShow = (): void => {
  isOverlayOpen.value = true;

  /**
   * PrimeVue attaches its ConnectedOverlayScrollHandler before it emits `show`, and this handler hides the overlay when any scrollable parent scrolls.
   * Remove this handler so the overlay stays open during scroll, because the overlay is added to the body and moves with the input.
   */
  pvAutoCompleteRef.value?.scrollHandler?.unbindScrollListener();
};

/**
 * Handles Escape key on the input.
 * - Non-empty query: clears the text and keeps the overlay open (lets the user browse all suggestions).
 * - Empty query: marks the hide as an explicit dismissal so `onHide` does not reopen the overlay.
 */
const onInputKeydown = (event: KeyboardEvent): void => {
  if (event.key !== 'Escape') return;

  if (props.query.length > 0) {
    debouncedTrack.cancel();
    emit('update:query', '');
    overlayHideIntent.value = 'escape-with-query';
  } else {
    overlayHideIntent.value = 'dismiss';
  }
};

onBeforeUnmount(() => {
  debouncedTrack.cancel();
});
</script>

<style scoped lang="scss">
@import '@/styles/premialab/scrollable-transparent';

.standard-autocomplete {
  :deep(.p-autocomplete-list-container) {
    @include scrollable-transparent(0);
    // Keep wheel scrolling inside the dropdown: without this, a scroll at the list's
    // top/bottom edge chains to the scrollable ancestor behind the popover.
    overscroll-behavior: contain;
  }

  max-width: 100%;

  :deep(.p-autocomplete-overlay) {
    max-width: 100%;
  }

  // PrimeVue `.p-inputtext` hardcodes `font-size: 1rem`; override with the app token.
  :deep(.p-inputtext),
  :deep(.p-autocomplete-option) {
    font-size: var(--p-component-commons-autocomplete-option-font-size);
    // Legacy Bootstrap input line-height conflicts with the themed size; keep it normal.
    line-height: normal;
  }

  :deep(.p-autocomplete-option) {
    font-weight: var(--p-component-commons-autocomplete-option-font-weight);
  }

  :deep(.p-autocomplete-option:has(.standard-autocomplete-group-header)) {
    opacity: 1;
  }

  .standard-autocomplete-group-header {
    color: var(--p-component-commons-autocomplete-submenu-label-color);
    font-weight: var(--p-navigation-submenu-label-font-weight);
  }
}
</style>
